import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { isAdminAuthenticated } from '@/lib/adminAuth';

export function BillingCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<null | { configured: boolean; totalLast30Days?: number; currency?: string; message?: string }>(null);

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      setLoading(true);
      try {
        const res = await fetch('/billing-summary');
        const json = await res.json();
        if (mounted) setData(json);
      } catch (e) {
        if (mounted) setData({ configured: false, message: 'Failed to fetch billing summary' });
      } finally {
        if (mounted) setLoading(false);
      }
    }
    if (isAdminAuthenticated()) fetchData();
    return () => { mounted = false; };
  }, []);

  if (!isAdminAuthenticated()) return null;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">Cloud Billing</h4>
          <p className="text-xs text-muted-foreground">Estimated usage (last 30 days)</p>
        </div>
        <div>
          {loading ? (
            <div className="text-sm">Loading…</div>
          ) : !data ? (
            <div className="text-sm text-muted-foreground">Not available</div>
          ) : !data.configured ? (
            <div className="text-sm">Not configured</div>
          ) : (
            <div className="text-right">
              <div className="text-lg font-semibold">{data.currency ?? 'USD'} {Number(data.totalLast30Days ?? 0).toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">Approx. last 30 days</div>
            </div>
          )}
        </div>
      </div>

      {!loading && data && !data.configured && (
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">To enable real-time billing metrics, export Cloud Billing to BigQuery and set the env vars <code>BILLING_BQ_PROJECT</code>, <code>BILLING_BQ_DATASET</code>, and <code>BILLING_BQ_TABLE</code> in your Functions config.</p>
          <div className="mt-2 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => {
              navigator.clipboard.writeText('See docs: https://cloud.google.com/billing/docs/how-to/export-data-bigquery')
                .then(() => toast({ title: 'Docs copied to clipboard', description: 'Billing export docs link copied' }))
                .catch(() => toast({ title: 'Copied', description: 'Docs link copied to clipboard' }));
            }}>Setup billing export</Button>
          </div>
        </div>
      )}
    </Card>
  );
}